import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Capitalizes the first letter of a string
 * @param text - The text to capitalize
 * @returns The text with the first letter capitalized
 */
export function capitalizeFirst(text: string): string {
  if (!text || text.length === 0) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Minor words that should be lowercase in title case (except at start/end)
 */
const TITLE_CASE_MINOR_WORDS = new Set(['a', 'an', 'the', 'and', 'but', 'or', 'for', 'nor', 'on', 'at', 'to', 'by', 'in', 'of', 'vs']);

/**
 * Converts a string to title case (capitalizes first letter of each word)
 * Preserves all-caps acronyms and special patterns like "10-day"
 * @param text - The text to convert to title case
 * @returns The text in title case
 */
export function toTitleCase(text: string): string {
  if (!text || text.length === 0) return text;

  return text
    .split(' ')
    .map((word, index) => {
      if (!word) return word;

      // Preserve numbers and patterns like "10-day"
      if (/^\d/.test(word)) return word;

      // Preserve all-caps words (likely acronyms)
      if (word.length > 1 && word === word.toUpperCase() && /^[A-Z]+$/.test(word)) {
        return word;
      }

      // Always capitalize first and last word
      if (index === 0 || TITLE_CASE_MINOR_WORDS.has(word.toLowerCase()) === false) {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }

      // Lowercase minor words in the middle
      return word.toLowerCase();
    })
    .join(' ');
} 